/**
 * LINE ニュース編集ビュー（同一ルートで一覧と切替、モーダル不可）。
 * 仕様書: docs/01_spec/spec_line_bot.md A-10.5
 */
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type {
  LineNewsSection,
  LineNewsSectionType,
} from '@goenhub/shared';
import {
  ArrowLeft,
  CalendarClock,
  RotateCcw,
  Save,
  Send,
  Sparkles,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { AddNewsSectionButton } from './AddNewsSectionButton';
import { NewsAiGenerateDialog } from './NewsAiGenerateDialog';
import { NewsDeliveryTab } from './NewsDeliveryTab';
import { NewsSectionGapInserter } from './NewsSectionGapInserter';
import { ScheduleNewsDialog } from './ScheduleNewsDialog';
import { SortableNewsSection } from './SortableNewsSection';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  getLineNews,
  useActiveScheduleForNews,
  useCancelLineNewsSchedule,
  useUpdateLineNews,
  type LineNewsRecord,
} from '@/hooks/useLineNews';
import { cn } from '@/lib/utils';



interface Props {
  newsId: string;
  onBack: () => void;
}

const SECTION_LABEL: Record<LineNewsSectionType, string> = {
  greeting: 'あいさつ',
  body: '本文',
  image: '画像',
  cta: 'CTA',
  closing: '締め',
};

type EditorTab = 'editor' | 'delivery';

export function NewsEditorView({ newsId, onBack }: Props) {
  const { user } = useAuth();
  const updateMutation = useUpdateLineNews();
  const cancelMutation = useCancelLineNewsSchedule();
  const { data: activeSchedule, refetch: refetchSchedule } =
    useActiveScheduleForNews(newsId);
  const [news, setNews] = useState<LineNewsRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<EditorTab>('editor');

  // ドラッグ&ドロップ並べ替え用センサー（メール側 Phase 22b と同パターン）
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !news) return;
    const oldIdx = news.sections.findIndex((s) => s.id === active.id);
    const newIdx = news.sections.findIndex((s) => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    setNews({ ...news, sections: arrayMove(news.sections, oldIdx, newIdx) });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getLineNews(newsId)
      .then((n) => {
        if (!cancelled) {
          setNews(n);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(`読み込み失敗: ${err instanceof Error ? err.message : String(err)}`);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [newsId]);

  if (loading || !news) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  const updateSection = (idx: number, next: LineNewsSection) => {
    setNews({
      ...news,
      sections: news.sections.map((s, i) => (i === idx ? next : s)),
    });
  };
  const deleteSection = (idx: number) => {
    setNews({
      ...news,
      sections: news.sections.filter((_, i) => i !== idx),
    });
  };
  /** 末尾に追加 */
  const insertSection = (type: LineNewsSectionType) => insertSectionAt(news.sections.length, type);

  /** insertAt 位置に挿入（中間挿入対応、末尾なら sections.length を渡す） */
  const insertSectionAt = (insertAt: number, type: LineNewsSectionType) => {
    const newSec: LineNewsSection = {
      id: `sec_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      type,
      label: SECTION_LABEL[type],
      content: '',
      ...(type === 'cta'
        ? { cta_url_placeholder: '', cta_button_label: '' }
        : {}),
    };
    const next = [...news.sections];
    next.splice(insertAt, 0, newSec);
    setNews({ ...news, sections: next });
  };

  const handleSave = async (status?: LineNewsRecord['status']) => {
    setSaving(true);
    try {
      await updateMutation.mutateAsync({
        id: news.id,
        data: {
          title: news.title,
          sections: news.sections,
          status: status ?? news.status,
          createdBy: news.createdBy || user?.uid || '',
        },
      });
      toast.success('保存しました');
    } catch (err) {
      toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * 配信予約済（または失敗状態）のニュースを下書き状態に戻す。
   * - status === 'scheduled' 時：cancelLineNewsSchedule で予約取消＋news doc を draft に戻す
   * - status === 'failed' 時：直接 news doc を draft に戻し lastError をクリア
   * - 'sending' / 'sent' は不可
   */
  const handleRevertToDraft = async () => {
    if (news.status === 'sending' || news.status === 'sent') {
      toast.error('送信中・送信済のニュースは下書きに戻せません');
      return;
    }
    if (
      !window.confirm(
        news.status === 'scheduled'
          ? '配信予約をキャンセルして下書きに戻します。よろしいですか？'
          : '失敗状態をクリアして下書きに戻します。よろしいですか？',
      )
    )
      return;
    setReverting(true);
    try {
      if (news.status === 'scheduled' && activeSchedule) {
        await cancelMutation.mutateAsync({
          scheduleId: activeSchedule.id,
          newsId: news.id,
        });
      } else {
        // failed 状態 or schedule doc が見つからない edge case：news doc を直接更新
        await updateMutation.mutateAsync({
          id: news.id,
          data: {
            status: 'draft',
            sendAt: null,
            lastError: null,
          },
        });
      }
      // 最新を再取得
      const next = await getLineNews(news.id);
      if (next) setNews(next);
      await refetchSchedule();
      toast.success('下書きに戻しました');
    } catch (err) {
      toast.error(
        `下書きに戻せません: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setReverting(false);
    }
  };

  const canSchedule =
    news.sections.length > 0 &&
    news.status !== 'sending' &&
    news.status !== 'sent' &&
    news.status !== 'scheduled';

  const canRevert =
    news.status === 'scheduled' || news.status === 'failed';

  // 配信タブは送信済 / 失敗時のみ表示
  const showDeliveryTab = news.status === 'sent' || news.status === 'failed';

  const tabClass = (isActive: boolean) =>
    cn(
      'px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors inline-flex items-center gap-2 cursor-pointer',
      isActive
        ? 'text-[var(--accent)] border-[var(--accent)]'
        : 'text-[var(--text-muted)] border-transparent hover:text-[var(--text-primary)]',
    );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="size-4 mr-1" />
          一覧へ戻る
        </Button>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setAiOpen(true)}
            disabled={saving || reverting || activeTab === 'delivery'}
          >
            <Sparkles className="size-3.5 mr-1" />
            AI 構成
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => handleSave()}
            disabled={
              saving || reverting || news.status === 'scheduled' || activeTab === 'delivery'
            }
            title={
              news.status === 'scheduled'
                ? '予約済みです。編集するには「下書きに戻す」を押してください'
                : undefined
            }
          >
            {saving ? <Spinner className="size-3 mr-1" /> : <Save className="size-3.5 mr-1" />}
            保存
          </Button>
          {canRevert ? (
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={handleRevertToDraft}
              disabled={reverting || saving}
              title={
                news.status === 'scheduled'
                  ? '配信予約をキャンセルして下書きに戻します'
                  : '失敗状態をクリアして下書きに戻します'
              }
            >
              {reverting ? (
                <Spinner className="size-3 mr-1" />
              ) : (
                <RotateCcw className="size-3.5 mr-1" />
              )}
              下書きに戻す
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={async () => {
                await handleSave();
                setScheduleOpen(true);
              }}
              disabled={!canSchedule || saving || reverting || activeTab === 'delivery'}
            >
              <CalendarClock className="size-3.5 mr-1" />
              配信予約
            </Button>
          )}
        </div>
      </div>

      {/* タブ（配信タブは送信済/失敗時のみ表示） */}
      {showDeliveryTab && (
        <div className="flex gap-1 border-b border-[var(--border)]">
          <button
            type="button"
            className={tabClass(activeTab === 'editor')}
            onClick={() => setActiveTab('editor')}
          >
            編集
          </button>
          <button
            type="button"
            className={tabClass(activeTab === 'delivery')}
            onClick={() => setActiveTab('delivery')}
          >
            配信
          </button>
        </div>
      )}

      {/* 配信タブ表示 */}
      {activeTab === 'delivery' && showDeliveryTab && (
        <NewsDeliveryTab news={news} />
      )}

      {/* 編集タブ表示（タブ非表示時もこちら）*/}
      {activeTab === 'editor' && (
        <>
      {/* 状態表示 */}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-[var(--text-muted)]">ステータス:</span>
        <span className="font-medium">{news.status}</span>
        {news.lastError && (
          <span className="text-[var(--accent-red)]">
            ⚠ {news.lastError}（試行 {news.attempts} 回）
          </span>
        )}
      </div>

      {news.status === 'scheduled' && (
        <div className="rounded border border-[var(--accent-amber)] bg-[var(--accent-amber-subtle)] px-3 py-2 text-xs text-[var(--accent-amber)] flex items-center gap-2">
          <CalendarClock className="size-3.5 flex-shrink-0" />
          <span>
            このニュースは配信予約済みです。編集するには「下書きに戻す」を押してください。
          </span>
        </div>
      )}
      {news.status === 'sending' && (
        <div className="rounded border border-[var(--accent)] bg-[var(--accent-subtle)] px-3 py-2 text-xs text-[var(--accent)] flex items-center gap-2">
          <Send className="size-3.5 flex-shrink-0" />
          <span>送信中です。完了までお待ちください。</span>
        </div>
      )}
      {news.status === 'sent' && (
        <div className="rounded border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-xs text-[var(--text-secondary)] flex items-center gap-2">
          <Send className="size-3.5 flex-shrink-0" />
          <span>送信済みです（読み取り専用）。</span>
        </div>
      )}

      <div>
        <Label className="text-xs">タイトル（管理画面表示用）</Label>
        <Input
          type="text"
          placeholder="例: 4月のキャンペーン告知"
          value={news.title}
          onChange={(e) => setNews({ ...news, title: e.target.value })}
        />
      </div>

      <div>
        <Label className="text-xs">セクション</Label>
        {news.sections.length === 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-[var(--text-muted)] italic py-3 text-center">
              セクション未追加。下のボタンから追加してください。
            </p>
            <AddNewsSectionButton onAdd={insertSection} />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleSectionDragEnd}
          >
            <SortableContext
              items={news.sections.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="pl-6">
                {/* 先頭の隙間（最初のセクションの上） */}
                <NewsSectionGapInserter
                  onInsert={(type) => insertSectionAt(0, type)}
                />
                {news.sections.map((sec, idx) => (
                  <div key={sec.id}>
                    <SortableNewsSection
                      newsId={news.id}
                      section={sec}
                      onChange={(next) => updateSection(idx, next)}
                      onDelete={() => deleteSection(idx)}
                    />
                    {/* このセクション直下の隙間 */}
                    <NewsSectionGapInserter
                      onInsert={(type) => insertSectionAt(idx + 1, type)}
                    />
                  </div>
                ))}
                <AddNewsSectionButton onAdd={insertSection} />
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <div className="rounded border border-[var(--border)] p-3 bg-[var(--bg-elevated)] text-xs space-y-1">
        <div className="flex items-center gap-1.5 text-[var(--text-secondary)]">
          <Send className="size-3" />
          <span className="font-semibold">LINE プレビュー（送信時のメッセージ分割）</span>
        </div>
        <div className="text-[10px] text-[var(--text-muted)]">
          連続したテキストセクションは結合され、画像は別メッセージとして送信されます。
        </div>
      </div>
        </>
      )}

      <ScheduleNewsDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        newsId={news.id}
        newsTitle={news.title}
        onScheduled={onBack}
      />

      <NewsAiGenerateDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        hasExistingSections={news.sections.length > 0}
        onApply={({ title, sections, modelUsed }) => {
          setNews({
            ...news,
            title: news.title || title,
            sections,
            modelUsed,
          });
        }}
      />
    </div>
  );
}
